import {
  EntityId,
  FulfillmentTypes,
  PageEntity,
  PrinticularProductTemplateEntity,
  PrinticularProductTemplateVariant,
  PrintServiceProductEntity,
  PrintServiceProductEntityHydrated,
  PrintServiceProductImageEntity,
} from "@jackfruit/common"
import { PayloadAction } from "@reduxjs/toolkit"
import { padStart, times } from "lodash"
import { SagaIterator } from "redux-saga"
import { all, call, put, takeEvery } from "redux-saga/effects"
import { CartEntity } from "~/interfaces/entities/Cart"
import { filterExistingImages } from "~/services/Utils"
import { actions, BootstrapProductFeedPayload } from "../process"
import { ApplicationState } from "../state/application"
import { blockProductFeedActions } from "../state/blockProductFeeds"
import { getApplication } from "./application"
import { getCurrentCart } from "./cart"
import { getCurrentPage, getPage } from "./page"
import { waitForPageSessionReady } from "./pageSession"
import { getPrintServiceProductImages } from "./printServiceProductImages"
import {
  getAllProductsForPageRegardlessOfFulfillment,
  getFulfillmentForProduct,
} from "./printServiceProducts"
import { processDownloadProductTemplate } from "./processDownloadProductTemplates"
import { updateFulfillment } from "./processUpdateFulfillment"
import { getProductTemplate } from "./productTemplates"
import { getProductTemplateVariantForProduct } from "./productTemplateVariants"

export function* watchBootstrapProductFeed() {
  yield takeEvery(actions.bootstrapProductFeed.type, bootstrapProductFeed)
}

function* bootstrapProductFeed(
  action: PayloadAction<BootstrapProductFeedPayload>
) {
  const { blockId, productId, templateId } = action.payload
  const errorPrefix = `BID#${blockId}, PID#${productId}, TID#${templateId}`

  try {
    yield put(blockProductFeedActions.setIsLoading(blockId, true))

    yield call(waitForPageSessionReady)

    const application: ApplicationState = yield call(getApplication)
    const cart: CartEntity = yield call(getCurrentCart)
    const page: PageEntity = yield call(getCurrentPage)
    const product: PrintServiceProductEntity = yield call(
      resolveProductForPage,
      {
        pageId: page.id,
        productId,
      }
    )

    const productImages: PrintServiceProductImageEntity[] = yield call(
      getPrintServiceProductImages,
      product.id
    )

    const productFulfillment: FulfillmentTypes = yield call(
      getFulfillmentForProduct,
      product.id
    )

    // update page fulfillment if required and possible
    if (cart.fulfillment !== productFulfillment) {
      yield call(updateFulfillment, {
        pageId: page.id,
        fulfillment: productFulfillment,
      })
    }

    let imageRegionsCount = product.minImages || 0

    let desktopIllustrationUrl = productImages.find(
      image => image?.metaData?.web?.desktop?.merchant
    )?.url

    let mobileIllustrationUrl = productImages.find(
      image => image?.metaData?.web?.mobile?.merchant
    )?.url

    let allFeaturedImagesForMobile: string[] = []
    let allFeaturedImagesForDesktop: string[] = []
    let allLifestyleImagesForMobile: string[] = []
    let allLifestyleImagesForDesktop: string[] = []

    if (mobileIllustrationUrl) {
      allFeaturedImagesForMobile.push(mobileIllustrationUrl)
    }

    if (desktopIllustrationUrl) {
      allFeaturedImagesForDesktop.push(desktopIllustrationUrl)
    }

    // download template from url parameter if any
    if (templateId) {
      yield call(processDownloadProductTemplate, templateId)

      const productTemplate: PrinticularProductTemplateEntity | null =
        yield call(getProductTemplate, templateId)

      if (productTemplate) {
        const variants: { variant: PrinticularProductTemplateVariant }[] =
          yield call(getProductTemplateVariantForProduct, {
            productId: product.id,
            productTemplateId: templateId,
          })

        if (variants.length > 0) {
          yield put(
            blockProductFeedActions.setTemplateId(blockId, Number(templateId))
          )
          imageRegionsCount = 0
          const variantToApply = variants[0].variant
          variantToApply.pages.forEach(page => {
            page.imageRegions.forEach(() => {
              imageRegionsCount += 1
            })
          })

          const { gmcProductImageryUrl } = application

          const countFeaturedImageForMobile = productImages.filter(
            image => image?.metaData?.web?.mobile?.merchantFrameFeatured
          ).length

          const countFeaturedImageForDesktop = productImages.filter(
            image => image?.metaData?.web?.desktop?.merchantFrameFeatured
          ).length

          const countLifestyleImagesForMobile = productImages.filter(
            image => image?.metaData?.web?.mobile?.merchantFrameLifestyle
          ).length

          const countLifestyleImagesForDesktop = productImages.filter(
            image => image?.metaData?.web?.desktop?.merchantFrameLifestyle
          ).length

          // reset all imagery
          allFeaturedImagesForMobile = []
          allFeaturedImagesForDesktop = []
          allLifestyleImagesForMobile = []
          allLifestyleImagesForDesktop = []

          if (countFeaturedImageForMobile > 0) {
            allFeaturedImagesForMobile.push(
              `${gmcProductImageryUrl}/${productId}/${templateId}/featured-mobile.jpg`
            )
          }

          if (countFeaturedImageForDesktop > 0) {
            allFeaturedImagesForDesktop.push(
              `${gmcProductImageryUrl}/${productId}/${templateId}/featured.jpg`
            )
          }

          times(countLifestyleImagesForMobile, index => {
            allLifestyleImagesForMobile.push(
              `${gmcProductImageryUrl}/${productId}/${templateId}/lifestyle_${padStart(
                String(index),
                2,
                "0"
              )}.jpg`
            )
          })

          times(countLifestyleImagesForDesktop, index => {
            allLifestyleImagesForDesktop.push(
              `${gmcProductImageryUrl}/${productId}/${templateId}/lifestyle_${padStart(
                String(index),
                2,
                "0"
              )}.jpg`
            )
          })

          // filter out 404 images
          const [
            allFeaturedImagesForMobileFiltered,
            allFeaturedImagesForDesktopFiltered,
            allLifestyleImagesForMobileFiltered,
            allLifestyleImagesForDesktopFiltered,
          ] = yield all([
            call(filterExistingImages, allFeaturedImagesForMobile),
            call(filterExistingImages, allFeaturedImagesForDesktop),
            call(filterExistingImages, allLifestyleImagesForMobile),
            call(filterExistingImages, allLifestyleImagesForDesktop),
          ])

          allFeaturedImagesForMobile = allFeaturedImagesForMobileFiltered
          allFeaturedImagesForDesktop = allFeaturedImagesForDesktopFiltered
          allLifestyleImagesForMobile = allLifestyleImagesForMobileFiltered
          allLifestyleImagesForDesktop = allLifestyleImagesForDesktopFiltered
        } else {
          console.warn("Template and product are not compatible")
        }
      }
    }

    yield put(
      blockProductFeedActions.setImagery(blockId, {
        mobile: {
          featured: allFeaturedImagesForMobile[0] || "",
          lifestyle: allLifestyleImagesForMobile,
        },
        desktop: {
          featured: allFeaturedImagesForDesktop[0] || "",
          lifestyle: allLifestyleImagesForDesktop,
        },
      })
    )

    yield put(blockProductFeedActions.setProductId(blockId, Number(product.id)))
    yield put(
      blockProductFeedActions.setImageRegionsCount(blockId, imageRegionsCount)
    )
    yield put(blockProductFeedActions.setIsLoading(blockId, false))
    yield put(blockProductFeedActions.setIsReady(blockId, true))
  } catch (error) {
    const errorMessage =
      error instanceof Error
        ? `${errorPrefix}: ${error.message}`
        : `${errorPrefix}: Unknown error on product feed block`

    console.error(errorMessage, error)

    yield put(blockProductFeedActions.setIsLoading(blockId, false))
    yield put(blockProductFeedActions.setHasError(blockId, true))
    yield put(blockProductFeedActions.setError(blockId, errorMessage))
  }
}

interface ResolveProductForPageOptions {
  pageId: EntityId
  productId?: EntityId
}

function* resolveProductForPage(
  options: ResolveProductForPageOptions
): SagaIterator<PrintServiceProductEntityHydrated> {
  const { pageId, productId } = options

  const allProductsByFulfillment: {
    delivery: PrintServiceProductEntityHydrated[]
    pickup: PrintServiceProductEntityHydrated[]
  } = yield call(getAllProductsForPageRegardlessOfFulfillment, pageId)

  const allProducts: PrintServiceProductEntityHydrated[] = Object.values(
    allProductsByFulfillment
  ).flat()

  let productToUse = null

  if (productId) {
    productToUse = allProducts.find(p => String(p.id) === String(productId))
  } else {
    console.warn(
      "Product unavailable, will use default product for page fulfillment"
    )
    const page: PageEntity = yield call(getPage, { pageId })
    const defaultFulfillment = page.defaultFulfillment

    const availableProductsForFuilfillment: PrintServiceProductEntityHydrated[] =
      allProductsByFulfillment[defaultFulfillment]

    productToUse = availableProductsForFuilfillment.sort(p => p.position)[0]
  }

  if (!productToUse) {
    throw new Error("Product not found for any fulfillment")
  }

  const { metaData } = productToUse
  const { merchantAvailability, gtin } = metaData || {}

  if (!merchantAvailability) {
    throw new Error("Product merchant availability is disabled")
  }

  if (!gtin) {
    throw new Error("Product gtin is missing")
  }

  return productToUse
}
